import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

// Actions
import { getAllCampaigns } from "../../actions/campaign/CampaignActions";

// Components
import Spinner from "../../components/spinner/Spinner";
import Pagination from "../pagination/Pagination";
import Hamburger from "../../components/hamburger-menu/Hamburger";
import TableHeaders from "../../components/table/TableHeaders";
import NoTableDataMessage from "../table/NoTableDataMessage";

// Utils and Constants
import { constructRedirectPath } from "../../utils/redirect-utils/RedirectUtils";
import {
  CampaignDemographicStatsStatus,
  CampaignReportStatus,
  DATE_FORMATS,
  UrlKeys,
} from "../../constants/GeneralConstants";
import { useUrlKeyCheck } from "../../mavin/utils/hooks/HooksUtil";
import {
  constructDateString,
  constructSingleDateString,
} from "../../common-utils/date-utils/DateUtils";
import { checkIsCoralMediaSpecific } from "../../common-utils/user-utils/userUtils";

// Urls
import { RedirectTo } from "../../urls/PageLinksURL";

// CSS
import "./CampaignTable.scss";

// Constants
function getTableHeaders(isCoralMediaSpecific = false) {
  const headersData = [
    {
      title: {
        displayName: "Campaign Name",
      },
      subTitle: {
        displayName: isCoralMediaSpecific ? "Campaign Type" : "",
        className: "sub-text",
      },
    },
    {
      title: {
        displayName: "Cities",
      },
    },
    {
      title: {
        displayName: "Sites",
      },
    },
    {
      title: {
        displayName: "Start and End Date",
      },
      subTitle: {
        displayName: "Last Updated Date",
        className: "sub-text",
      },
    },

    {
      title: {
        displayName: "Actions",
      },
    },
  ];

  return headersData;
}

const Loader = () => {
  return (
    <tr className="text-center">
      <td colSpan={6}>
        <Spinner />
      </td>
    </tr>
  );
};

function CampaignActionsCell({ campaign = {} }) {
  // Campaign Data
  const {
    id: campaignId,
    reportStatus,
    isPlan,
    isCompleted,
    isMonitoringEnabled,
    isMonitoringOnly,
    demographicStatisticsStatus,
  } = campaign;

  // Urls
  const campaignPlanOverviewPageUrl =
    RedirectTo.campaignPlanOverviewPageUrl.replace(":campaignId", campaignId);
  const monitorCampaignViewPageUrl =
    RedirectTo.monitorCampaignViewPageUrl.replace(":campaignId", campaignId);
  const campaignViewPageUrl = RedirectTo.campaignViewPageUrl.replace(
    ":campaignId",
    campaignId
  );
  const campaignGraphReportPageUrl =
    RedirectTo.graphReportsCampaignPageUrl.replace(":campaignId", campaignId);

  // is campaign report generated
  const isCampaignReportGenerated =
    reportStatus === CampaignReportStatus.GENERATED;

  // checking is mantaray specific
  const isMantaraySpecific = useUrlKeyCheck(UrlKeys.mantaraySpecific);

  // checking Demographic stats processed
  const isDemographicStatsProcessed =
    demographicStatisticsStatus === CampaignDemographicStatsStatus.PROCESSED;

  // booleans
  const isShowGraphReportColumn = !isMantaraySpecific;
  const isShowGraphReportLink =
    !isMonitoringOnly &&
    isCampaignReportGenerated &&
    isDemographicStatsProcessed;

  return (
    <td>
      <div className="d-flex justify-content-between">
        {/** Planning */}
        <div className="camp-link">
          {isPlan && !isCompleted && (
            <Link to={constructRedirectPath(campaignPlanOverviewPageUrl)}>
              Continue Planning
            </Link>
          )}
        </div>

        {/** Monitoring */}
        <div className="camp-link">
          {(isPlan || isMonitoringEnabled) && (
            <Link to={constructRedirectPath(monitorCampaignViewPageUrl)}>
              {isCompleted ? "View" : "Manage"} Monitoring
            </Link>
          )}
        </div>

        {/** Report */}
        <div className="camp-link">
          {!isMonitoringOnly && (
            <Link to={constructRedirectPath(campaignViewPageUrl)}>
              {isCampaignReportGenerated ? "View Report" : "Generate Report"}
            </Link>
          )}
        </div>

        {/** Graph Report : hiding option for mantaray */}
        {isShowGraphReportColumn && (
          <div className="camp-link">
            {isShowGraphReportLink && (
              <Link to={constructRedirectPath(campaignGraphReportPageUrl)}>
                Graph Report
              </Link>
            )}
          </div>
        )}

        <div className="d-none">
          <Hamburger>
            <button className="dropdown-item">Download Report</button>
            <button className="dropdown-item d-none">Share Link</button>
            <button className="dropdown-item">Archive</button>
          </Hamburger>
        </div>
      </div>
    </td>
  );
}

function CampaignDateCell({ campaign = {} }) {
  const { updatedOn, startTimestamp, endTimestamp } = campaign || {};

  // Constructing the Date String
  const dateString = constructDateString(
    startTimestamp,
    endTimestamp,
    DATE_FORMATS.full_month_with_date_year
  );

  // updatedOn Date String
  const updateOnDateString = constructSingleDateString(
    updatedOn,
    DATE_FORMATS.full_month_with_date_year
  );

  return (
    <td>
      <p className="mb-0">{dateString}</p>
      <small className="text-muted">{updateOnDateString}</small>
    </td>
  );
}

function CampaignTitleCell({ campaign = {}, isCoralMediaSpecific = false }) {
  const { title, campaignType = "" } = campaign || {};

  return (
    <td>
      <p className="mb-0">{title}</p>

      {/* Coral Media : Campaign Type */}
      {isCoralMediaSpecific && campaignType && (
        <small className="text-muted">{campaignType}</small>
      )}
    </td>
  );
}

function CampaignsTableRow({ campaign = {}, isCoralMediaSpecific }) {
  const { cityCount, mediaCount } = campaign || {};

  return (
    <tr>
      <CampaignTitleCell
        campaign={campaign}
        isCoralMediaSpecific={isCoralMediaSpecific}
      />
      <td>{cityCount}</td>
      <td>{mediaCount}</td>
      <CampaignDateCell campaign={campaign} />
      <CampaignActionsCell campaign={campaign} />
    </tr>
  );
}

function CampaignsTableBody({
  loading,
  isCampaignsPresent,
  campaigns,
  isCoralMediaSpecific,
}) {
  if (loading) {
    return <Loader />;
  }

  //if there is no campaign
  if (!isCampaignsPresent) {
    return <NoTableDataMessage colSpan={6} message="No campaigns present" />;
  }

  return (
    <>
      {campaigns.map((campaign, i) => (
        <CampaignsTableRow
          key={campaign.id}
          campaign={campaign}
          i={i}
          isCoralMediaSpecific={isCoralMediaSpecific}
        />
      ))}
    </>
  );
}

// Table Component
function CampaignsTable({
  campaigns,
  isCampaignsPresent,
  loading,
  pagination,
  searchText = "",
}) {
  const dispatch = useDispatch();

  // data
  const { totalCount, pageNo, pageSize } = pagination;

  // function
  function loadCampaignList(pageNumber, pageSizeCount) {
    dispatch(getAllCampaigns(searchText, pageNumber, pageSizeCount));
  }

  // coral media
  const isCoralMediaSpecific = checkIsCoralMediaSpecific();

  // table headers
  const tableHeaders = getTableHeaders(isCoralMediaSpecific);

  return (
    <div className="table-responsive mt-3">
      <table className="table border table-reports">
        <TableHeaders
          scope={"col"}
          tableHeaders={tableHeaders}
          headerClass={"thead bg-light"}
        />
        <tbody>
          <CampaignsTableBody
            campaigns={campaigns}
            isCampaignsPresent={isCampaignsPresent}
            loading={loading}
            isCoralMediaSpecific={isCoralMediaSpecific}
          />
        </tbody>
      </table>

      <Pagination
        totalCount={totalCount}
        pageNo={pageNo}
        pageSize={pageSize}
        onClick={loadCampaignList}
      />
    </div>
  );
}

export default CampaignsTable;
